import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ZoomIn } from 'lucide-react';

const GallerySection = () => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('All');

  const galleryImages = [
    {
      id: 1,
      src: 'https://customer-assets.emergentagent.com/job_ayurtech-hub/artifacts/n1ooln56_Treatment%20Center.jpg',
      titleKey: 'gallery.treatmentCenter',
      category: 'Facilities'
    },
    {
      id: 2,
      src: 'https://customer-assets.emergentagent.com/job_ayurmed-global/artifacts/fw1hyxvp_Modern%20Imaging%20Technology.jpg',
      titleKey: 'gallery.modernImaging',
      category: 'Facilities'
    },
    {
      id: 3,
      src: 'https://customer-assets.emergentagent.com/job_ayurmed-global/artifacts/v3eh41ab_Allopathic%20Treatment.jpg',
      titleKey: 'gallery.allopathicTreatment',
      category: 'Facilities'
    },
    {
      id: 4,
      src: 'https://customer-assets.emergentagent.com/job_ayurmed-global/artifacts/kobqgm5b_Ayurvedic%20Manufacturing%20Facility.jpg',
      titleKey: 'gallery.ayurvedicManufacturing',
      category: 'Facilities'
    },
    {
      id: 5,
      src: 'https://customer-assets.emergentagent.com/job_ayurmed-global/artifacts/73gfrg06_Lab%20Setup.jpg',
      titleKey: 'gallery.labSetup',
      category: 'Facilities'
    },
    {
      id: 6,
      src: 'https://customer-assets.emergentagent.com/job_ayurtech-hub/artifacts/tqo9ofvy_Doctor%20AI%20Image.png',
      titleKey: 'gallery.digitalAvatar',
      category: 'Technology'
    },
    {
      id: 7,
      src: 'https://customer-assets.emergentagent.com/job_ayurtech-hub/artifacts/5sw8984u_panchakarma%20therapy%20room.jpeg',
      titleKey: 'gallery.panchakarma',
      category: 'Facilities'
    },
    {
      id: 8,
      src: 'https://customer-assets.emergentagent.com/job_ayurtech-hub/artifacts/p6iute7l_IMG-20251223-WA0054.jpg',
      titleKey: 'gallery.training1',
      category: 'Training'
    },
    {
      id: 9,
      src: 'https://customer-assets.emergentagent.com/job_ayurtech-hub/artifacts/b8ssk05l_IMG-20251223-WA0036.jpg',
      titleKey: 'gallery.training2',
      category: 'Training'
    },
    {
      id: 10,
      src: 'https://customer-assets.emergentagent.com/job_ayurtech-hub/artifacts/gijvth9g_IMG-20251223-WA0046.jpg',
      titleKey: 'gallery.training3',
      category: 'Training'
    },
    {
      id: 11,
      src: 'https://customer-assets.emergentagent.com/job_ayurtech-hub/artifacts/iowy2876_IMG-20251223-WA0045.jpg',
      titleKey: 'gallery.training4',
      category: 'Training'
    },
    {
      id: 12,
      src: 'https://customer-assets.emergentagent.com/job_ayurmed-global/artifacts/j2g09n84_IMG-20251223-WA0042.jpg',
      titleKey: 'gallery.training5',
      category: 'Training'
    },
    {
      id: 13,
      src: 'https://customer-assets.emergentagent.com/job_ayurmed-global/artifacts/utk89prh_IMG-20251223-WA0037.jpg',
      titleKey: 'gallery.training6',
      category: 'Training'
    },
    {
      id: 14,
      src: 'https://images.pexels.com/photos/7089401/pexels-photo-7089401.jpeg?auto=compress&cs=tinysrgb&w=800',
      titleKey: 'gallery.aiDiagnostics',
      category: 'Technology'
    },
  ];

  const categories = [
    { key: 'All', labelKey: 'gallery.all' },
    { key: 'Facilities', labelKey: 'gallery.facilities' },
    { key: 'Training', labelKey: 'gallery.training' },
    { key: 'Technology', labelKey: 'gallery.technology' }
  ];

  const filteredImages = filter === 'All' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === filter);

  const openLightbox = (image) => {
    setSelectedImage(image);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <section
      id="gallery"
      style={{
        background: '#111827',
        padding: '5rem 1.2rem',
        position: 'relative',
      }}
    >
      <div className="container">
        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem', maxWidth: '700px', margin: '0 auto 3rem' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
            {t('gallery.title')}
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            {t('gallery.subtitle')}
          </p>
        </div>

        {/* Filter Buttons */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '1rem', 
          marginBottom: '2.5rem',
          flexWrap: 'wrap'
        }}>
          {categories.map((category) => (
            <button
              key={category.key}
              onClick={() => setFilter(category.key)}
              style={{
                padding: '0.6rem 1.5rem',
                borderRadius: '2rem',
                border: filter === category.key ? '2px solid #22c55e' : '2px solid rgba(255, 255, 255, 0.2)',
                background: filter === category.key 
                  ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(34, 197, 94, 0.1) 100%)' 
                  : 'rgba(255, 255, 255, 0.05)',
                color: filter === category.key ? '#22c55e' : 'var(--text-secondary)',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(8px)',
              }}
            >
              {t(category.labelKey)}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
          }}
        >
          {filteredImages.map((image) => (
            <div
              key={image.id}
              onClick={() => openLightbox(image)}
              style={{
                position: 'relative',
                borderRadius: '1rem',
                overflow: 'hidden',
                cursor: 'pointer',
                aspectRatio: '4/3',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(34, 197, 94, 0.2)',
                transition: 'all 0.3s ease',
              }}
              className="hover-lift"
            >
              {/* Image */}
              <img
                src={image.src}
                alt={t(image.titleKey)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease',
                }}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              />

              {/* Overlay */}
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: '1.5rem',
                  background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.8))',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                }}
              >
                <div>
                  <span
                    style={{
                      display: 'inline-block',
                      background: 'rgba(34, 197, 94, 0.3)',
                      color: '#22c55e',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '1rem',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      marginBottom: '0.5rem',
                      border: '1px solid rgba(34, 197, 94, 0.5)',
                    }}
                  >
                    {t(`gallery.${image.category.toLowerCase()}`)}
                  </span>
                  <h3 style={{ 
                    color: 'white', 
                    fontSize: '1.1rem', 
                    fontWeight: '600',
                    textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                  }}>
                    {t(image.titleKey)}
                  </h3>
                </div>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'rgba(34, 197, 94, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(34, 197, 94, 0.5)',
                  }}
                >
                  <ZoomIn size={18} color="#22c55e" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          onClick={closeLightbox}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '2rem',
          }}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            style={{
              position: 'absolute',
              top: '2rem',
              right: '2rem',
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
            }}
          >
            <X size={24} />
          </button>

          {/* Image Container */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: '85vh',
              position: 'relative',
            }}
          >
            <img
              src={selectedImage.src}
              alt={t(selectedImage.titleKey)}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '0.5rem',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              }}
            />
            <div style={{ 
              textAlign: 'center', 
              marginTop: '1.5rem',
              color: 'white'
            }}>
              <span
                style={{
                  display: 'inline-block',
                  background: 'rgba(34, 197, 94, 0.3)',
                  color: '#22c55e',
                  padding: '0.3rem 1rem',
                  borderRadius: '1rem',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  marginBottom: '0.75rem',
                  border: '1px solid rgba(34, 197, 94, 0.5)',
                }}
              >
                {t(`gallery.${selectedImage.category.toLowerCase()}`)}
              </span>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                {t(selectedImage.titleKey)}
              </h3>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default GallerySection;
